import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { useLanguage } from "../../../../../LanguageProvider";
import { useToaster } from '../../../../../ToasterProvider';
import Services from "../../../../../services/Services";
import { GeneralContext } from "../../../../../GeneralProvider";
import AlertBox from "../../../../../components/AlertBox/AlertBox";
import UniversalTable from "../../../../../components/UniversalTable/UniversalTable";

export default function SocialHiveRoomGroupSettings({ selectedChannel, updateSettings }) {
    const { getTranslation } = useLanguage();
    const { toastSuccess, toastError } = useToaster();
    const { getMyChannels } = useContext(GeneralContext);

    const [editingGroupID, setEditingGroupID] = useState(null);
    const [groupEditTitle, setGroupEditTitle] = useState("");

    const [editingRoomID, setEditingRoomID] = useState(null);
    const [roomEditTitle, setRoomEditTitle] = useState("");
    const [roomPermissions, setRoomPermissions] = useState({});

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertData, setAlertData] = useState(null);

    const handleToggleRoomPermission = (roomID) => {
        const token = localStorage.getItem("token");
        const currentValue = roomPermissions[roomID];
        const newValue = !currentValue;

        // Güncel state'i hemen güncelle
        setRoomPermissions(prev => ({ ...prev, [roomID]: newValue }));

        // API'ye gönder
        const apiValue = newValue ? false : true; // true => 0 (herkes yazabilir), false => 1 (sadece admin)
        Services.SocialHiveUpdateRoomUserCanText(token, selectedChannel.channelID, roomID, apiValue)
            .then(res => {
                if (res.data.status) {
                    toastSuccess(getTranslation("successMessage"));
                } else {
                    toastError(getTranslation("somethingWentWrong"));
                }
            })
            .catch(() => {
                toastError(getTranslation("somethingWentWrong"));
            });
    };

    const handleEditGroupTitle = (groupID) => {
        const updatedGroups = groups.map(g =>
            g.groupID === groupID ? { ...g, title: groupEditTitle } : g
        );
        setGroups(updatedGroups);

        const newSettings = {
            ...selectedChannel.settings,
            roomGroups: updatedGroups
        };

        updateSettings(newSettings);
        setEditingGroupID(null);
        toastSuccess(getTranslation("successMessage"));
    };

    const handleEditRoomTitle = async (roomID) => {
        const token = localStorage.getItem("token");

        try {
            const res = await Services.SocialHiveUpdateRoomName(
                token,
                selectedChannel.channelID,
                roomID,
                roomEditTitle
            );

            if (res.data.status) {
                // İki seçenekten biri:

                // 1) Kanalları tazele (tercihim bu; rooms isimleri üstten doğru gelir)
                await getMyChannels?.();

                // 2) Veya mevcut settings yapısını bozmadan geri gönder:
                // updateSettings({
                //   ...selectedChannel.settings,
                //   roomGroups: groups, // sadece roomGroups'u güncelliyorsak bile tam settings dön
                // });

                setEditingRoomID(null);
                toastSuccess(getTranslation("successMessage"));
            } else {
                toastError(getTranslation("somethingWentWrong"));
            }
        } catch (e) {
            toastError(getTranslation("somethingWentWrong"));
        }
    };

    const rawGroups = selectedChannel?.settings?.roomGroups || [];
    const allRooms = selectedChannel?.rooms || [];
    const validRoomIDs = new Set(allRooms.map(r => r.roomID));

    useEffect(() => {
        const initialPermissions = {};
        allRooms.forEach(room => {
            initialPermissions[room.roomID] = room.canUsersSendMessage === "1" ? false : true;
        });
        setRoomPermissions(initialPermissions);
    }, [allRooms]);

    const cleanedGroups = (Array.isArray(rawGroups)
        ? rawGroups
        : Array.isArray(rawGroups?.roomGroups)
            ? rawGroups.roomGroups
            : rawGroups
                ? [rawGroups]
                : []
    ).map(group => ({
        ...group,
        roomOrder: Array.isArray(group.roomOrder)
            ? group.roomOrder.filter(id => validRoomIDs.has(id))
            : []
    }));

    const [groups, setGroups] = React.useState(cleanedGroups);

    useEffect(() => {
        const rawGroups = selectedChannel?.settings?.roomGroups || [];
        const allRooms = selectedChannel?.rooms || [];
        const validRoomIDs = new Set(allRooms.map(r => r.roomID));

        const updatedGroups = (Array.isArray(rawGroups)
            ? rawGroups
            : Array.isArray(rawGroups?.roomGroups)
                ? rawGroups.roomGroups
                : rawGroups
                    ? [rawGroups]
                    : []
        ).map(group => ({
            ...group,
            roomOrder: Array.isArray(group.roomOrder)
                ? group.roomOrder.filter(id => validRoomIDs.has(id))
                : []
        }));

        setGroups(updatedGroups);
    }, [selectedChannel?.settings?.roomGroups, selectedChannel.rooms]);

    const handleConfirmDelete = () => {
        if (alertData?.type === "group") {
            handleRemoveGroup(alertData.id);
        } else if (alertData?.type === "room") {
            handleRemoveRoom(alertData.id);
        }
        setAlertOpen(false);
        setAlertData(null);
    };

    const handleCancelDelete = () => {
        setAlertOpen(false);
        setAlertData(null);
    };

    const handleRemoveGroup = (groupID) => {
        const token = localStorage.getItem("token");
        const groupToRemove = groups.find(g => g.groupID === groupID);

        if (!groupToRemove) return;

        const promises = groupToRemove.roomOrder.map(roomID =>
            Services.SocialHiveRemoveRoom(token, selectedChannel.channelID, roomID)
        );

        Promise.all(promises)
            .then(() => {
                const updated = groups.filter(g => g.groupID !== groupID);
                setGroups(updated);

                // 🛠️ settings objesini tamamen güncelle
                const newSettings = {
                    ...selectedChannel.settings,
                    roomGroups: updated
                };
                getMyChannels()
                updateSettings(newSettings); // ✅ tüm settings gönderiliyor
                toastSuccess(getTranslation("successMessage"));
            })
            .catch(() => {
                toastError(getTranslation("somethingWentWrong"));
            });
    };

    const handleRemoveRoom = (roomID) => {
        const token = localStorage.getItem("token");

        Services.SocialHiveRemoveRoom(token, selectedChannel.channelID, roomID)
            .then((res) => {
                if (res.data.status) {
                    const updatedGroups = groups.map(group => ({
                        ...group,
                        roomOrder: group.roomOrder.filter(id => id !== roomID)
                    }));
                    setGroups(updatedGroups);

                    const newSettings = {
                        ...selectedChannel.settings,
                        roomGroups: updatedGroups
                    };

                    updateSettings(newSettings); // ✅ tam settings güncellemesi
                    toastSuccess(getTranslation("successMessage"));
                } else {
                    toastError(getTranslation("somethingWentWrong"));
                }
            })
            .catch(() => {
                toastError(getTranslation("somethingWentWrong"));
            });
    };

    const getGroupName = (roomID) => {
        const found = groups.find(g => g.roomOrder.includes(roomID));
        return found ? found.title : getTranslation("general");
    };

    const groupsColumns = useMemo(() => ([
        {
            key: 'title',
            header: getTranslation("groupName"),
            render: (row) =>
                editingGroupID === row.groupID ? (
                    <input
                        className="api-title-input"
                        value={groupEditTitle}
                        onChange={(e) => setGroupEditTitle(e.target.value)}
                        onBlur={() => handleEditGroupTitle(row.groupID)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleEditGroupTitle(row.groupID, groupEditTitle);
                            }
                        }}
                        autoFocus
                    />
                ) : (
                    <span
                        onClick={() => {
                            setEditingGroupID(row.groupID);
                            setGroupEditTitle(row.title);
                        }}
                    >
                        {row.title}
                    </span>
                )
        },
        {
            key: 'roomCount',
            header: getTranslation("roomCount"),
            headerClassName: "text-center",
            align: 'center',
            render: (row) => (Array.isArray(row.roomOrder) ? row.roomOrder.length : 0)
        },
        {
            key: 'actions',
            header: '',
            width: 48,
            align: 'right',
            headerAlign: 'right',
            render: (row) => (
                <FiTrash2
                    onClick={() => {
                        setAlertData({ type: "group", id: row.groupID });
                        setAlertOpen(true);
                    }}
                    style={{ cursor: "var(--pointer)" }}
                />
            )
        },
    ]), [editingGroupID, groupEditTitle, getTranslation, groups]);

    const groupsData = useMemo(() => groups, [groups]);

    // Rooms table columns
    const roomsColumns = useMemo(() => ([
        {
            key: 'title',
            header: getTranslation("roomName"),
            render: (row) =>
                editingRoomID === row.roomID ? (
                    <input
                        className="api-title-input"
                        value={roomEditTitle}
                        onChange={(e) => setRoomEditTitle(e.target.value)}
                        onBlur={() => handleEditRoomTitle(row.roomID)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleEditRoomTitle(row.roomID, roomEditTitle);
                            }
                        }}
                        autoFocus
                    />
                ) : (
                    <span
                        onClick={() => {
                            setEditingRoomID(row.roomID);
                            setRoomEditTitle(row.title);
                        }}
                    >
                        {row.title}
                    </span>
                )
        },
        {
            key: 'group',
            header: getTranslation("group"),
            render: (row) => getGroupName(row.roomID)
        },
        {
            key: 'onlyAdmins',
            header: getTranslation("onlyAdminsCanSendMessage"),
            align: 'center',
            headerClassName: "text-center",
            render: (row) => (
                <input
                    type="checkbox"
                    className="sh-settings-permission-checkbox"
                    checked={!!roomPermissions[row.roomID]}
                    onChange={() => handleToggleRoomPermission(row.roomID)}
                />
            )
        },
        {
            key: 'actions',
            header: '',
            width: 48,
            align: 'right',
            headerAlign: 'right',
            render: (row) => (
                <FiTrash2
                    onClick={() => {
                        setAlertData({ type: "room", id: row.roomID });
                        setAlertOpen(true);
                    }}
                    style={{ cursor: "var(--pointer)" }}
                />
            )
        },
    ]), [editingRoomID, roomEditTitle, roomPermissions, getTranslation, groups]);

    const roomsData = useMemo(() => allRooms, [allRooms]);

    const groupRowKey = (row) => String(row.groupID);
    const roomRowKey = (row) => String(row.roomID);


    return (
        <div className="sh-settings-main">
            <div className="sh-settings-content">
                <span className="sh-settings-title">{getTranslation("groups")}</span>
                <UniversalTable
                    data={groupsData}
                    columns={groupsColumns}
                    getRowKey={groupRowKey}
                    tableClassName="sh-settings-table"
                    emptyText={getTranslation("noData")}
                />

                <div style={{ marginTop: '30px' }}>
                    <span className="sh-settings-title" >{getTranslation("rooms")}</span>
                    <UniversalTable
                        data={roomsData}
                        columns={roomsColumns}
                        getRowKey={roomRowKey}
                        tableClassName="sh-settings-table"
                        emptyText={getTranslation("noData")}
                    />
                </div>

                <AlertBox
                    isOpen={alertOpen}
                    type="warning"
                    title={getTranslation("areYouSure")}
                    message={
                        alertData?.type === "group"
                            ? getTranslation("groupDeleteConfirmMessage")
                            : getTranslation("roomDeleteConfirmMessage")
                    }
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    confirmText={getTranslation("delete")}
                    cancelText={getTranslation("cancel")}
                />
            </div>
        </div>
    );
}